import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  FlagIcon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  MapIcon,
  MegaphoneIcon,
  WindowIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navigation = [
    { name: '대시보드', href: '/admin/dashboard', icon: HomeIcon },
    { name: '맛집 정보 관리', href: '/admin/restaurants', icon: BuildingOfficeIcon },
    { name: '기관 정보 관리', href: '/admin/agencies', icon: BuildingOfficeIcon },
    { name: '방문기록 관리', href: '/admin/visit-records', icon: CalendarIcon },
    { name: '리뷰 관리', href: '/admin/reviews', icon: ChatBubbleLeftRightIcon },
    { name: '게시글 관리', href: '/admin/posts', icon: DocumentTextIcon },
    { name: '의견제안 관리', href: '/admin/suggestions', icon: DocumentTextIcon },
    { name: '게시판/카테고리 관리', href: '/admin/boards', icon: DocumentTextIcon },
    { name: '게시글 신고 관리', href: '/admin/post-reports', icon: FlagIcon },
    { name: '포인트/등급 관리', href: '/admin/points', icon: CurrencyDollarIcon },
    { name: '사용자 관리', href: '/admin/users', icon: UserGroupIcon },
    // { name: '추천 시스템', href: '/admin/recommendations', icon: SparklesIcon },
    { name: '약관 관리', href: '/admin/terms', icon: DocumentTextIcon },
    { name: '지역 순서 관리', href: '/admin/region-order', icon: MapIcon },
    { name: '데이터 품질 관리', href: '/admin/data-quality', icon: ExclamationTriangleIcon },
    { name: '데이터 업로드/다운로드', href: '/admin/data-upload', icon: ArrowUpTrayIcon },
    { name: 'Google Ads 연동', href: '/admin/google-ads', icon: MegaphoneIcon },
    { name: '팝업설정', href: '/admin/popup-settings', icon: WindowIcon },

    // { name: '통계/랭킹', href: '/admin/statistics', icon: ChartBarIcon },
    // { name: '설정/공지', href: '/admin/settings', icon: Cog6ToothIcon },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 모바일 사이드바 */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-gray-800">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-lg font-semibold text-white">관리자 패널</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-300 hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* 데스크톱 사이드바 */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-gray-800">
          <div className="flex h-16 items-center px-4">
            <h1 className="text-lg font-semibold text-white">관리자 패널</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="lg:pl-64">
        {/* 헤더 */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="relative flex flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="검색..."
                className="block h-full w-full border-0 py-0 pl-10 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-x-4 lg:gap-x-6">
            <button className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
              <BellIcon className="h-6 w-6" />
            </button>

            {/* 프로필 드롭다운 */}
            <div className="relative">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-700">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="hidden lg:block">
                  <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                  <p className="text-xs text-gray-500">관리자</p>
                </div>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center text-gray-400 hover:text-gray-500"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 페이지 콘텐츠 */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 
